import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { distinctUntilChanged, map, startWith } from 'rxjs'

import { QueryParams } from 'app/static/query-params.enum'

import { RippleDirective } from '../ripple/ripple.directive'
import { IconComponent } from '../svg/icon.component'
import { TranslatePipe } from '../translate.pipe'

@Component({
  selector: 'pai-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, TranslatePipe, RouterModule, RippleDirective, IconComponent],
})
export class PaginationComponent {
  readonly pageCount = input.required<number>()

  protected showLowerDots = computed<boolean>(() => {
    return this.pageCount() > 7 && this.currentPage() > 4
  })
  protected showUpperDots = computed<boolean>(() => {
    return this.pageCount() > 7 && this.currentPage() < this.pageCount() - 3
  })

  protected showPrevPageBtn = computed<boolean>(() => {
    return this.pageCount() > 2 && this.currentPage() > 1
  })
  protected showNextPageBtn = computed(() => {
    return this.pageCount() > 2 && this.currentPage() < this.pageCount()
  })

  protected readonly middleItems = computed<number[]>(() => {
    return this.pageCount() <= 7
      ? [2, 3, 4, 5, 6].slice(0, this.pageCount() - 2)
      : this.showLowerDots()
        ? this.showUpperDots()
          ? [-1, 0, 1].map((v) => this.currentPage() + v)
          : [4, 3, 2, 1].map((v) => this.pageCount() - v)
        : [1, 2, 3, 4].map((v) => v + 1)
  })

  protected readonly currentPage = signal<number>(1)

  constructor() {
    inject(ActivatedRoute)
      .queryParamMap.pipe(
        takeUntilDestroyed(),
        map((params) => parseInt(params.get(QueryParams.PAGE) || '1', 10) || 1),
        startWith(1),
        distinctUntilChanged(),
      )
      .subscribe((v) => this.currentPage.set(v))
  }

  protected getParams(page: number) {
    return {
      [QueryParams.PAGE]: page,
    }
  }
}
